import { useMemo } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  FindingTitleCell,
  FindingTypeCell,
  AssetsCountCell,
  LastUpdatedCell,
  RiskSummaryCell,
  RiskSummaryValue,
  FindingStatusIndicatorCell,
  LegacyActionCell,
} from "~/components/DataGridCells";
import { Header } from "~/components/FirewatchPage/FirewatchPageColumns";
import { FindingsTableSort } from "~/components/FirewatchList/components/FindingsTableSort";
import { FindingsOrderField } from "~/operations";
import { NoMatchFilters } from "~/pages/inventory/components/EmptyState/NoMatchFilters";
import { EmptySection } from "~/components/vulnerabilities/empty-section";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import type {
  ActionContext,
  FindingsDataGridTableProps,
  FindingsColDef,
} from "./types";

const headerStyles = {
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "normal",
  color: "text.primary",
};

function buildColumns(
  hiddenColumns: string[] = [],
  actionContext: ActionContext,
  useStatusTags: boolean,
): FindingsColDef[] {
  const hidden = new Set(hiddenColumns);
  const allColumns: FindingsColDef[] = [
    {
      field: "title",
      headerName: "Finding",
      headerClassName: "left-cell",
      cellClassName: "left-cell",
      flex: 1,
      minWidth: 300,
      resizable: false,
      sortable: false,
      valueGetter: (_value, row) => row.title || row.id,
      renderCell: (params) => <FindingTitleCell value={params.value} />,
    },
    {
      field: "type",
      headerName: "Type",
      width: 120,
      resizable: false,
      sortable: false,
      valueGetter: (_value, row) => row.__typename,
      renderCell: (params) => <FindingTypeCell value={params.value} />,
    },
    {
      field: "assets",
      headerName: "Assets",
      width: 80,
      resizable: false,
      sortable: false,
      align: "left",
      headerAlign: "left",
      // Placeholder - data not available yet
      valueGetter: () => null,
      renderCell: (params) => <AssetsCountCell value={params.value} />,
    },
    {
      field: "lastUpdated",
      headerName: "Last updated",
      width: 120,
      resizable: false,
      sortable: true,
      renderCell: (params) => <LastUpdatedCell value={params.value} />,
    },
    {
      field: "riskSummary",
      headerName: "Risk summary",
      headerClassName: "right-cell",
      width: 180,
      resizable: false,
      sortable: true,
      sortField: FindingsOrderField.RiskValue,
      valueGetter: (_value, row): RiskSummaryValue => ({
        rating: row.rating,
        riskValue: row.riskValue,
        riskFactors: row.riskFactors,
      }),
      renderCell: (params) => <RiskSummaryCell value={params.value} />,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      cellClassName: "right-cell",
      resizable: false,
      sortable: false,
      renderCell: (params) =>
        useStatusTags ? (
          <FindingStatusIndicatorCell
            row={params.row}
            actionContext={actionContext}
          />
        ) : (
          // TODO: Remove LegacyActionCell branch when "Finding status tags" feature flag is removed
          <LegacyActionCell row={params.row} actionContext={actionContext} />
        ),
    },
  ];
  return allColumns.filter((col) => !hidden.has(col.field));
}

export function FindingsDataGridTable({
  rows,
  loading = false,
  onRowClick,
  rowSelectionModel,
  onRowSelectionModelChange,
  sort,
  totalCount,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  hiddenColumns = [],
  activeFilterCount = 0,
  onResetFilters,
  assetId,
  spaceId,
  jobId,
  assetMrn,
  assetName,
}: FindingsDataGridTableProps) {
  const actionContext = useMemo(
    () => ({ assetId, spaceId, jobId, assetMrn, assetName }),
    [assetId, spaceId, jobId, assetMrn, assetName],
  );
  const { isFeatureEnabled } = useFeatureFlags();
  const useStatusTags = isFeatureEnabled("Finding status tags");

  const columns = useMemo(
    () => buildColumns(hiddenColumns, actionContext, useStatusTags),
    [hiddenColumns, actionContext, useStatusTags],
  );

  // Derive sortable headers from column definitions for FindingsTableSort.
  // Filters columns that have sortable: true and a sortField (GraphQL field mapping),
  // then maps them to the Header format expected by FindingsTableSort.
  const sortableHeaders: Header[] = useMemo(() => {
    return columns
      .filter(
        (col): col is FindingsColDef & { sortField: FindingsOrderField } =>
          col.sortable === true && !!col.sortField,
      )
      .map((col) => ({
        id: col.field,
        field: col.sortField,
        label: col.headerName || col.field,
        sortable: true,
      }));
  }, [columns]);

  const displayCount = rows.length;
  const total = totalCount ?? rows.length;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/* Header bar - only render if sort is provided */}
      {sort && sortableHeaders.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            px: 1,
          }}
        >
          {loading ? (
            <Skeleton variant="text" width={120} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {displayCount} of {total} items
            </Typography>
          )}
          <FindingsTableSort sort={sort} sortableHeaders={sortableHeaders} />
        </Box>
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.mrn}
        columnHeaderHeight={48}
        rowHeight={54}
        checkboxSelection
        disableRowSelectionOnClick
        disableColumnMenu
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        onRowClick={onRowClick}
        slots={{
          noRowsOverlay:
            activeFilterCount > 0
              ? () => <FilteredNoRowsOverlay onResetFilters={onResetFilters} />
              : NoRowsOverlay,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton",
          },
        }}
        sx={{
          ...(rows.length === 0 && !loading && { minHeight: 400 }),
          borderRadius: 2,
          border: "none",
          backgroundColor: "background.paper",
          [`& .${gridClasses.columnSeparator}`]: {
            [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
              display: "none",
            },
          },
          [`& .${gridClasses.columnHeader}`]: {
            ...headerStyles,
            backgroundColor: "background.dark",
            borderBottom: "none !important",
          },
          [`& .${gridClasses.sortIcon}`]: {
            fontSize: "10px",
            color: "text.primary",
          },
          [`& .${gridClasses.footerContainer}`]: {
            backgroundColor: "background.dark",
            borderTop: "none !important",
            backgroundImage: "none",
            opacity: 1,
          },
          [`& .${gridClasses.columnHeader}:focus`]: {
            outline: "none",
          },
          [`& .${gridClasses.columnHeader}:focus-within`]: {
            outline: "none",
          },
          [`& .${gridClasses.cell}:focus`]: {
            outline: "none",
          },
          [`& .${gridClasses.cell}:focus-within`]: {
            outline: "none",
          },
          "& .MuiDataGrid-row--borderBottom": {
            borderBottom: "none",
          },
          "& .left-cell": {
            pl: 3,
          },
          "& .right-cell": {
            pr: 3,
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowCount={rowCount}
        pageSizeOptions={[10, 25, 50]}
      />
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        backgroundColor: "background.paper",
      }}
    >
      <EmptySection text="No findings found" />
    </Box>
  );
}

function FilteredNoRowsOverlay({
  onResetFilters,
}: {
  onResetFilters?: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        backgroundColor: "background.paper",
      }}
    >
      <NoMatchFilters
        title="No findings match your filter selection"
        content="Out of the findings, none match the filters you selected. Reset them to reveal all findings."
        contained={false}
        onClick={() => onResetFilters?.()}
      />
    </Box>
  );
}
